// BuildingMaterials.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import PortlandComent from "../images/construction/Portland-cement.png";
import IronRod from "../images/construction/Iron-Rod.jpg";
import Granite from "../images/construction/granite.jpg";
import RiverSand from "../images/construction/River-Sand.jpg";
import PvcPipes from "../images/construction/PVC-Pipes.jpg";
import ElectricalWires from "../images/construction/Electrical-Wire.jpg";
import { Helmet } from "react-helmet";

const BuildingMaterials = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Sample data for building materials
  const materials = [
    {
      id: 1,
      name: "Portland Cement",
      category: "cement",
      price: "₦4,500 per bag",
      supplier: "Dangote Cement",
      image: PortlandComent,
      description: "High-quality Portland cement for construction",
      inStock: true,
    },
    {
      id: 2,
      name: "TMT Iron Rods",
      category: "steel",
      price: "₦450,000 per ton",
      supplier: "African Steel",
      image: IronRod,
      description: "Thermo-Mechanically Treated iron rods",
      inStock: true,
    },
    {
      id: 3,
      name: "Granite Stones",
      category: "aggregates",
      price: "₦35,000 per ton",
      supplier: "Stoneworks Ltd",
      image: Granite,
      description: "20mm granite stones for concrete",
      inStock: true,
    },
    {
      id: 4,
      name: "River Sand",
      category: "sand",
      price: "₦25,000 per truck",
      supplier: "Natural Resources Co.",
      image: RiverSand,
      description: "Clean river sand for construction",
      inStock: false,
    },
    {
      id: 5,
      name: "PVC Pipes",
      category: "plumbing",
      price: "₦2,500 per length",
      supplier: "Pipe Masters",
      image: PvcPipes,
      description: "3-inch PVC pipes for plumbing",
      inStock: true,
    },
    {
      id: 6,
      name: "Electrical Wires",
      category: "electrical",
      price: "₦8,000 per roll",
      supplier: "Spark Electricals",
      image: ElectricalWires,
      description: "2.5mm copper electrical wires",
      inStock: true,
    },
  ];

  const categories = [
    { id: "all", name: "All Materials" },
    { id: "cement", name: "Cement" },
    { id: "steel", name: "Steel & Iron" },
    { id: "aggregates", name: "Aggregates" },
    { id: "sand", name: "Sand" },
    { id: "plumbing", name: "Plumbing" },
    { id: "electrical", name: "Electrical" },
    { id: "tiles", name: "Tiles & Flooring" },
    { id: "paint", name: "Paints" },
  ];

  const services = [
    {
      title: "Bulk Delivery",
      description:
        "We deliver building materials in bulk quantities to your construction site",
      icon: "🚚",
    },
    {
      title: "Quality Assurance",
      description: "All materials are certified and meet industry standards",
      icon: "✅",
    },
    {
      title: "Competitive Pricing",
      description:
        "Get the best prices from verified suppliers and manufacturers",
      icon: "💰",
    },
    {
      title: "Expert Advice",
      description:
        "Consult with our construction experts for material selection",
      icon: "👷",
    },
  ];

  const filteredMaterials = materials.filter((material) => {
    const matchesCategory =
      selectedCategory === "all" || material.category === selectedCategory;
    const matchesSearch =
      material.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      material.supplier.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gray-50">
       <Helmet>
              <title>Building Materials | EstateGhar</title>
              <meta
                name="description"
                content="Find quality building materials and supplies for your construction projects across Pakistan"
              />
              <meta
                name="keywords"
                content="building materials, construction supplies, cement, steel, sand, aggregates, plumbing, electrical, EstateGhar"
              />
            </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-lime-600 to-lime-800 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Building Materials & Supplies
            </h1>
            <p className="text-xl mb-8">
              Quality construction materials from trusted suppliers across
              Pakistan
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search for materials, suppliers, or brands..."
                  className="w-full px-6 py-4 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-lime-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button className="absolute right-2 top-2 bg-lime-600 text-white px-6 py-2 rounded-lg hover:bg-lime-700 transition-colors">
                  Search
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold mb-6 text-center">
            Material Categories
          </h2>
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((category) => (
              <button
                key={category.id}
                className={`px-4 py-2 rounded-full font-medium transition-all ${
                  selectedCategory === category.id
                    ? "bg-lime-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Materials Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMaterials.map((material) => (
              <div
                key={material.id}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="h-48 bg-gray-200 relative">
                  <img
                    src={material.image}
                    alt={material.name}
                    className="w-full h-full object-cover"
                  />
                  {!material.inStock && (
                    <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded text-sm">
                      Out of Stock
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2">{material.name}</h3>
                  <p className="text-gray-600 mb-3">{material.description}</p>
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm text-gray-500">
                      {material.supplier}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      const phoneNumber = "+923487172565"; // Replace with your WhatsApp number (without + sign)
                      const message = material.inStock
                        ? `Hello, I am interested in buying ${material.name}. Please provide more details.`
                        : "Please notify me when this material is available.";
                      window.open(
                        `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
                          message
                        )}`,
                        "_blank"
                      );
                    }}
                    className="w-full bg-lime-500 text-white py-2 rounded-lg hover:bg-lime-600 transition-colors"
                  >
                    {material.inStock
                      ? "Contact to buy"
                      : "Notify When Available"}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredMaterials.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">
                No materials found matching your criteria.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Our Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div key={index} className="text-center p-6">
                <div className="text-4xl mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-lime-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl mb-8">
            Get all your building materials in one place with guaranteed quality
            and competitive prices.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                const phoneNumber = "+923487172565"; // Replace with your WhatsApp number
                const message = "Hello, I want to register as a supplier.";
                window.open(
                  `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
                    message
                  )}`,
                  "_blank"
                );
              }}
              className="bg-white text-lime-600 px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors"
            >
              Become a Supplier
            </button>

            <button
              onClick={() => {
                const phoneNumber = "+923487172565"; // Replace with your WhatsApp number
                const message =
                  "Hello, I would like to talk to your sales team.";
                window.open(
                  `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
                    message
                  )}`,
                  "_blank"
                );
              }}
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-bold hover:bg-white hover:text-lime-600 transition-colors"
            >
              Contact Sales
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl mx-auto space-y-4">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-bold mb-2">How do I place an order?</h3>
              <p className="text-gray-600">
                Simply browse our catalog, add items to your cart, and proceed
                to checkout. We'll contact you to confirm delivery details.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-bold mb-2">Do you deliver to my location?</h3>
              <p className="text-gray-600">
                We deliver to most locations across Pakistan. Delivery costs
                vary based on location and quantity.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-bold mb-2">Are your materials certified?</h3>
              <p className="text-gray-600">
                Yes, all our materials come from certified suppliers and meet
                Pakistann industrial standards.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BuildingMaterials;
